import { useLocation, useNavigate } from '@remix-run/react';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  Box,
  Group,
  Text,
  Button,
  Burger,
  Drawer,
  Stack,
  UnstyledButton,
} from '@mantine/core';
import { IconLogout } from '@tabler/icons-react';
import { ColorSchemeToggle } from '~/components/ColorSchemeToggle';

const PUBLIC_ROUTES = [
  '/',
  '/login',
  '/register/email',
  '/register/verify-prompt',
  '/auth/verify',
  '/verify-email',
];

const NAV_LINKS = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Activities', path: '/activities' },
];

export function NavigationBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [opened, { toggle, close }] = useDisclosure(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (PUBLIC_ROUTES.includes(location.pathname)) {
    return null;
  }

  const handleNavigate = (path: string) => {
    navigate(path);
    close();
  };

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
      bg="var(--mantine-color-body)"
      bd="0 0 1px 0 var(--mantine-color-default-border)"
      px="md"
      py="sm"
    >
      {isMobile ? (
        <Group justify="space-between" align="center" h={40}>
          <UnstyledButton onClick={() => handleNavigate('/dashboard')}>
            <Text fw={700} size="lg">
              SOMTB
            </Text>
          </UnstyledButton>
          <Burger
            opened={opened}
            onClick={toggle}
            aria-label={opened ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={opened}
            size="sm"
          />
          <Drawer
            opened={opened}
            onClose={close}
            title="Navigation"
            padding="md"
            size="xs"
            position="right"
          >
            <Stack gap="sm">
              {NAV_LINKS.map((link) => (
                <Button
                  key={link.path}
                  variant={location.pathname === link.path ? 'light' : 'subtle'}
                  fullWidth
                  justify="flex-start"
                  onClick={() => handleNavigate(link.path)}
                  aria-current={location.pathname === link.path ? 'page' : undefined}
                >
                  {link.label}
                </Button>
              ))}
              <ColorSchemeToggle labelled />
              <Button
                variant="subtle"
                color="red"
                fullWidth
                justify="flex-start"
                leftSection={<IconLogout size={16} />}
                onClick={() => handleNavigate('/logout')}
              >
                Logout
              </Button>
            </Stack>
          </Drawer>
        </Group>
      ) : (
        <Group justify="space-between" align="center" h={40}>
          <Group gap="lg">
            <UnstyledButton onClick={() => handleNavigate('/dashboard')}>
              <Text fw={700} size="lg">
                SOMTB
              </Text>
            </UnstyledButton>
            {NAV_LINKS.map((link) => (
              <Button
                key={link.path}
                variant={location.pathname === link.path ? 'light' : 'subtle'}
                onClick={() => handleNavigate(link.path)}
                aria-current={location.pathname === link.path ? 'page' : undefined}
              >
                {link.label}
              </Button>
            ))}
          </Group>
          <Group gap="xs">
            <ColorSchemeToggle />
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconLogout size={16} />}
              onClick={() => handleNavigate('/logout')}
            >
              Logout
            </Button>
          </Group>
        </Group>
      )}
    </Box>
  );
}
